import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type QualityStatus = 'passed' | 'failed';

type QualityCheckResult = {
  status: QualityStatus;
  errors?: string[];
};

const WEBCAM_PREFIX = 'SYSTEM_CHECK_STEPPER.WEBCAM';
const LUMNIANCE_THRESHOLD = 40;
const BLURRINESS_THRESHOLD = 100;

export const useImageQuality = () => {
  const { t } = useTranslation('translation', { keyPrefix: WEBCAM_PREFIX });

  const getAverageLuminance = useCallback((img: HTMLImageElement | HTMLCanvasElement): number => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context not available');
    canvas.width = img.width;
    canvas.height = img.height;
    ctx.drawImage(img, 0, 0, img.width, img.height);
    const { data } = ctx.getImageData(0, 0, img.width, img.height);

    let totalLuminance = 0;
    const pixelCount = data.length / 4;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      totalLuminance += luminance;
    }

    return totalLuminance / pixelCount; // 0-255
  }, []);

  const getBlurriness = useCallback((img: HTMLImageElement | HTMLCanvasElement): number => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context not available');

    canvas.width = img.width;
    canvas.height = img.height;
    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, img.width, img.height);

    const gray = [];
    for (let i = 0; i < imageData.data.length; i += 4) {
      const avg = (imageData.data[i] + imageData.data[i + 1] + imageData.data[i + 2]) / 3;
      gray.push(avg);
    }

    // Simple Laplacian kernel approximation:
    const width = img.width;
    const laplacian = [];
    for (let y = 1; y < img.height - 1; y++) {
      for (let x = 1; x < img.width - 1; x++) {
        const i = y * width + x;
        const value = 4 * gray[i] - gray[i - 1] - gray[i + 1] - gray[i - width] - gray[i + width];
        laplacian.push(value);
      }
    }

    const mean = laplacian.reduce((a, b) => a + b, 0) / laplacian.length;
    const variance = laplacian.reduce((a, b) => a + (b - mean) * (b - mean), 0) / laplacian.length;

    return variance; // Lower variance → blurrier
  }, []);

  const checkImageQuality = async (img: HTMLImageElement | HTMLCanvasElement): Promise<QualityCheckResult> => {
    const luminance = getAverageLuminance(img);
    const blurriness = await getBlurriness(img);

    const errors: string[] = [];

    if (luminance < LUMNIANCE_THRESHOLD) {
      errors.push(`${WEBCAM_PREFIX}.WEBCAMANALYSISERRORS.DARK`);
    }

    if (blurriness < BLURRINESS_THRESHOLD) {
      errors.push(`${WEBCAM_PREFIX}.WEBCAMANALYSISERRORS.BLUR`);
    }

    if (errors.length > 0) {
      return {
        status: 'failed',
        errors,
      };
    }

    return {
      status: 'passed',
    };
  };

  return {
    getAverageLuminance,
    getBlurriness,
    checkImageQuality,
  };
};
